import { type CookieHandlerInput } from 'supertokens-web-js/lib/build/cookieHandler/types';

const frontendCookiesKey = 'st-cookies';

interface Dict {
  [key: string]: string;
}

const inMemoryStorage: Dict = {};

function setKeyValue(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    inMemoryStorage[key] = value;
  }
}

function getKeyValue(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    if (inMemoryStorage[key] === undefined) {
      return null;
    }
    return inMemoryStorage[key];
  }
}

function getCookiesFromStorage(): string {
  const cookiesFromStorage = getKeyValue(frontendCookiesKey);

  if (cookiesFromStorage === null) {
    setKeyValue(frontendCookiesKey, '[]');
    return '';
  }

  /**
   * Because we store cookies in local storage, we need to manually check
   * for expiry before returning all cookies
   */
  let cookieArrayInStorage = [];
  try {
    cookieArrayInStorage = JSON.parse(cookiesFromStorage);
  } catch {
    // TODO: If we fail to parse the localstorage data we just return empty and continue.
  }

  const cookieArrayToReturn = [];
  for (
    let cookieIndex = 0;
    cookieIndex < cookieArrayInStorage.length;
    cookieIndex += 1
  ) {
    const currentCookieString = cookieArrayInStorage[cookieIndex];
    const parts = currentCookieString.split(';');
    let expirationString = '';

    for (let partIndex = 0; partIndex < parts.length; partIndex += 1) {
      const currentPart = parts[partIndex];

      if (currentPart.toLocaleLowerCase().includes('expires=')) {
        expirationString = currentPart;
        break;
      }
    }

    if (expirationString !== '') {
      const expirationValueString = expirationString.split('=')[1];
      const expirationDate = new Date(expirationValueString);
      const currentTimeInMillis = Date.now();

      // if the cookie has expired, we skip it
      if (expirationDate.getTime() >= currentTimeInMillis) {
        cookieArrayToReturn.push(currentCookieString);
      }
    } else {
      cookieArrayToReturn.push(currentCookieString);
    }
  }

  /**
   * After processing and removing expired cookies we need to update the cookies
   * in storage so we dont have to process the expired ones again
   */
  try {
    setKeyValue(frontendCookiesKey, JSON.stringify(cookieArrayToReturn));
  } catch (err) {
    console.error('failed to serialize cookie', err);
  }

  return cookieArrayToReturn.join('; ');
}

function setCookieToStorage(cookieString: string): void {
  const cookieName = cookieString.split(';')[0].split('=')[0];
  const cookiesFromStorage = getKeyValue(frontendCookiesKey);
  let cookiesArray = [];

  if (cookiesFromStorage !== null) {
    try {
      cookiesArray = JSON.parse(cookiesFromStorage);
    } catch {
      // If we fail to parse the localstorage data we just return empty and continue.
    }
  }

  let cookieIndex = -1;
  for (let i = 0; i < cookiesArray.length; i += 1) {
    const currentCookie = cookiesArray[i];

    if (currentCookie.indexOf(`${cookieName}=`) !== -1) {
      cookieIndex = i;
      break;
    }
  }

  /**
   * If a cookie with the same name already exists (index != -1) then we
   * need to remove the old value and replace it with the new one.
   *
   * If it does not exist then simply add the new cookie
   */
  if (cookieIndex !== -1) {
    cookiesArray[cookieIndex] = cookieString;
  } else {
    cookiesArray.push(cookieString);
  }

  try {
    setKeyValue(frontendCookiesKey, JSON.stringify(cookiesArray));
  } catch (err) {
    console.error('failed to serialize cookie', err);
  }
}

export const getCookieHandler: CookieHandlerInput = (original) => ({
  ...original,
  async getCookie() {
    return getCookiesFromStorage();
  },
  async setCookie(cookieString) {
    setCookieToStorage(cookieString);
  },
});
